import * as styles from '../FirstResponder.module.css';
import { LiveConsoleViewModel, LiveMode } from '../LivePreview/liveViewModel';

/*
 * Canonical presentation component (GRAVY-10373 convergence, Phase 1 extraction).
 *
 * Extracted verbatim from LiveConsole.tsx's page header (title/subtitle + mode badge + Change
 * mode + Stop) so both the frozen preview and the real connected page render the exact same
 * markup/testids.
 *
 * GRAVY-10373 Task 3: "Change mode" is gone. Each REQUESTED mode renders exactly its own legal
 * button(s) — never a control that would perform an illegal transition, and never more than one
 * mode button at a time:
 *   off    -> Start Shadow
 *   shadow -> Go Live (+ Stop)
 *   live   -> Back to Shadow (+ Stop)
 *
 * GRAVY-10373 C1: the button set (and Stop, computed by the caller) derive from REQUESTED mode —
 * what is ARMED — never effective mode. A gate-halted mode (effectiveMode 'off' while
 * requestedMode is not) still renders the button set for its requested mode, since the backend's
 * already_in_mode check also judges against the requested/configured mode, and offering a
 * "Start Shadow" the backend would refuse is worse than offering nothing new. The badge shows the
 * requested mode and appends a "· halted" marker (with an explanatory title) exactly when a gate
 * has halted it.
 *
 * Safety only ever gates ESCALATION. Go Live requires safety proven `clear` with the
 * `shadow_zero_action_proof` basis specifically (I4) — `clear` via `effective_off` proves nothing
 * about Shadow and must not unlock Go Live — and no request already in flight. Stepping DOWN to
 * Shadow (from OFF or from LIVE) is always available regardless of safety, exactly like Stop: a
 * safety failure must never remove the safer action. This component still owns NO transition
 * policy beyond that gating — the caller (Connected/index.tsx) is the one that turns a click into
 * a real POST, via onRequestModeTransition(target).
 */

const MODE_BADGE: Record<LiveMode, { label: string; cls?: string }> = {
  live: { label: 'LIVE', cls: styles.green },
  shadow: { label: 'SHADOW', cls: styles.amber },
  off: { label: 'OFF', cls: undefined },
};

const HALTED_TITLE =
  'A safety gate has halted this mode — nothing can act until it is resolved. First Responder is still on, so Stop remains available.';

type Props = {
  vm: LiveConsoleViewModel | null;
  // GRAVY-10373 C1: Stop follows REQUESTED MODE alone (nothing to stop at OFF) — computed by the
  // caller. Never shares a flag with the mode buttons below: see the module comment.
  stopDisabled: boolean;
  // A mode POST is in flight. Disables every mode button (never a double-submit) — Go Live is
  // additionally gated on safety, below.
  modePending: boolean;
  onRequestModeTransition: (target: 'shadow' | 'live') => void;
  onStopClick: () => void;
  // GRAVY-10816 bug 2: a role that may read the console but not operate it (Helpdesk, per AC-6)
  // sees the mode badge and a Read-only marker, and none of the mode/Stop buttons. The backend
  // refuses those writes for that role anyway; this keeps the page honest about it instead of
  // offering a button that 403s. Optional so the preview (no signed-in admin) is unaffected.
  readOnly?: boolean;
  // Prod 2026-09-21: with no pilot configuration the backend still accepts Start Shadow (a mode is
  // two flags, not a configuration) and every tick then halts on an empty cohort. The console holds
  // the button until a configuration exists, and says why on the button itself.
  startShadowBlockedReason?: string | null;
};

export const READ_ONLY_TITLE =
  'Your role can view this console. Starting or stopping a mode, changing pilot settings and running a tick are Tenant Admin actions.';

const ConsoleHeader = ({
  vm,
  stopDisabled,
  modePending,
  onRequestModeTransition,
  onStopClick,
  readOnly = false,
  startShadowBlockedReason = null,
}: Props) => {
  const safety = vm?.safety;
  // I4: `clear` alone is not enough — the backend requires the `shadow_zero_action_proof` basis
  // specifically before it will accept Go Live. A `clear` via `effective_off` proves only that
  // nothing CAN happen while off, never that Shadow ran clean.
  const shadowProofClear =
    !!safety &&
    safety.state === 'clear' &&
    safety.basis === 'shadow_zero_action_proof';
  const goLiveDisabled = modePending || !shadowProofClear;
  const goLiveTitle = modePending
    ? 'Changing mode…'
    : !shadowProofClear
      ? 'Run Shadow mode cleanly first, with no actions taken, before going Live.'
      : undefined;
  const halted =
    !!vm && vm.effectiveMode === 'off' && vm.requestedMode !== 'off';

  return (
    <div className={styles.pageHeader}>
      <div>
        <h2 className={styles.title}>First Responder</h2>
        {vm && (
          <div className={styles.subtitle} data-testid="flp-fixture-note">
            {vm.fixtureNote}
          </div>
        )}
      </div>
      <div className={styles.headerControls}>
        {vm && (
          <span
            className={`${styles.badge} ${MODE_BADGE[vm.requestedMode].cls ?? ''}`}
            data-testid="flp-mode-badge"
            title={halted ? HALTED_TITLE : undefined}
          >
            {MODE_BADGE[vm.requestedMode].label}
            {halted ? ' · halted' : ''}
          </span>
        )}
        {readOnly && (
          <span
            className={styles.badge}
            data-testid="flp-read-only"
            title={READ_ONLY_TITLE}
          >
            Read-only
          </span>
        )}
        {!readOnly && vm && vm.requestedMode === 'off' && (
          <button
            type="button"
            className={styles.btn}
            data-testid="flp-mode-start-shadow"
            disabled={modePending || !!startShadowBlockedReason}
            title={startShadowBlockedReason ?? undefined}
            onClick={() => onRequestModeTransition('shadow')}
          >
            Start Shadow
          </button>
        )}
        {!readOnly && vm && vm.requestedMode === 'shadow' && (
          <button
            type="button"
            className={styles.btn}
            data-testid="flp-mode-go-live"
            disabled={goLiveDisabled}
            title={goLiveTitle}
            onClick={() => onRequestModeTransition('live')}
          >
            Go Live
          </button>
        )}
        {!readOnly && vm && vm.requestedMode === 'live' && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline}`}
            data-testid="flp-mode-back-to-shadow"
            disabled={modePending}
            onClick={() => onRequestModeTransition('shadow')}
          >
            Back to Shadow
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            data-testid="flp-stop-button"
            disabled={stopDisabled}
            onClick={onStopClick}
          >
            Stop First Responder
          </button>
        )}
      </div>
    </div>
  );
};

export default ConsoleHeader;
